import { useState } from 'react';
import axios from 'axios';
import { ChevronRightIcon, ChevronDownIcon } from '@radix-ui/react-icons';

import { ErrorPage } from '../../../ErrorPage';
import { PrivatePayrollAuctionLoading } from '../Loading';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { cpfMask } from '../../../../utils/utils';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import { useAnimatedModal } from '../../../../utils/hooks/useAnimatedModal';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField } from '../components/CheckboxField';
import * as S from './styles';
import { Warning3Icon } from '@qitech/icons';

interface ResumeOperationProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
}

const formatDate = (inputDate: string): string => {
    const [year, month, day] = inputDate.split('-');
    return `${day}/${month}/${year}`;
};

const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

const formatPercent = (value: number): string =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

// ── Coverage risk display data ───────────────────────────

const COVERED_RISKS: Record<string, any> = {
    accidental_death: {
        title: 'Morte por acidente',
        description: 'A morte do segurado, sempre que esta esteja relacionada a causas acidentais.',
    },
    permanent_disability: {
        hasTopics: true,
        standard: {
            title: 'Invalidez acidental',
            description: 'A invalidez permanente total por acidente do segurado, decorrente de lesões físicas ocorridas por causas acidentais.',
        },
        Akad_Seguros1: {
            title: 'Invalidez Permanente Total por Acidente (IPTA)',
            description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
        },
        Akad_Seguros2: {
            title: 'Invalidez Permanente Total por Acidente (IPTA)',
            description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
        },
        Akad_Seguros3: {
            title: 'Invalidez Permanente Total por Acidente (IPTA)',
            description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
        },
    },
    funeral_assistance: {
        title: 'Auxílio Funeral',
        description: 'Auxílio para o funeral do segurado, independente da causa mortis, na forma de reembolso de custos.',
    },
    medical_assistance: {
        title: 'Assistência em Medicamentos',
        description: 'Pronto atendimento para indicar farmácias cadastradas e até 80% de desconto em medicamentos.',
    },
    accidental_death_fgts: {
        hasTopics: true,
        standard: {
            title: 'Morte por acidente',
            description: 'Morte do segurado, decorrente de causas acidentais ou acidente pessoal ocorrido durante a vigência do seguro.',
        },
        '180_seguros': {
            title: 'Morte por acidente',
            description: 'Morte do segurado, decorrente de causas acidentais ou acidente pessoal ocorrido durante a vigência do seguro.',
        },
    },
    income_loss_fgts: {
        hasTopics: true,
        standard: {
            title: 'Perda de Renda',
            description: 'Perda de salário do segurado por desemprego involuntário (CLT). Perda de renda do segurado por incapacidade física total e temporária (autônomo).',
        },
        '180_seguros': {
            title: 'Perda de Renda',
            description: 'Perda de salário do segurado por desemprego involuntário (CLT). Perda de renda do segurado por incapacidade física total e temporária (autônomo).',
        },
    },
    permanent_disability_fgts: {
        hasTopics: true,
        standard: {
            title: 'Invalidez por acidente',
            description: 'A invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
        },
        '180_seguros': {
            title: 'Invalidez por acidente',
            description: 'A invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
        },
    },
    mqc_death: {
        hasTopics: true,
        standard: {
            title: 'Morte Qualquer Causa (MQC)',
            description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
        },
        Akad_Seguros1: {
            title: 'Morte Qualquer Causa (MQC)',
            description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
        },
        Akad_Seguros2: {
            title: 'Morte Qualquer Causa (MQC)',
            description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
        },
        Akad_Seguros3: {
            title: 'Morte Qualquer Causa (MQC)',
            description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
        },
    },
    ipta_disability: {
        hasTopics: true,
        standard: {
            title: 'Invalidez Permanente Total por Acidente (IPTA)',
            description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
        },
        Akad_Seguros1: {
            title: 'Invalidez Permanente Total por Acidente (IPTA)',
            description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
        },
        Akad_Seguros2: {
            title: 'Invalidez Permanente Total por Acidente (IPTA)',
            description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
        },
        Akad_Seguros3: {
            title: 'Invalidez Permanente Total por Acidente (IPTA)',
            description: 'Invalidez permanente total por acidente do segurado, decorrente de lesões físicas por causas acidentais durante a vigência do seguro.',
        },
    },
    involuntary_unemployment: {
        hasTopics: true,
        standard: {
            title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
            description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
        },
        Akad_Seguros1: {
            title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
            description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
        },
        Akad_Seguros2: {
            title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
            description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
        },
        Akad_Seguros3: {
            title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
            description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
        },
    },
    unemployment: {
        hasTopics: true,
        standard: {
            title: 'Ajuda no desemprego',
            description: 'Até 6 pagamentos de R$ 1.000 se perder o emprego involuntariamente (demissão sem justa causa).',
        },
        Zurich1: {
            title: 'Ajuda no desemprego',
            description: 'Até 6 pagamentos de R$ 1.000 se perder o emprego involuntariamente (demissão sem justa causa).',
        },
        Akad_Seguros1: {
            title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
            description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
        },
        Akad_Seguros2: {
            title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
            description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
        },
        Akad_Seguros3: {
            title: 'Desemprego Involuntário (CLT) / Incapacidade Física Total e Temporária (autônomos)',
            description: 'Perda de salário do segurado (empregado CLT) por desemprego involuntário ou incapacidade física total e temporária para autônomos.',
        },
    },
    death: {
        hasTopics: true,
        standard: {
            title: 'Morte',
            description: 'Sua família recebe até R$ 20.000 se você falecer.',
        },
        Zurich1: {
            title: 'Morte',
            description: 'Sua família recebe até R$ 20.000 se você falecer.',
        },
        Akad_Seguros1: {
            title: 'Morte Qualquer Causa (MQC)',
            description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
        },
        Akad_Seguros2: {
            title: 'Morte Qualquer Causa (MQC)',
            description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
        },
        Akad_Seguros3: {
            title: 'Morte Qualquer Causa (MQC)',
            description: 'Morte do segurado, decorrente de causas acidentais ou naturais, ocorrida durante a vigência do seguro.',
        },
    },
    permanent_disability_illness: {
        hasTopics: true,
        standard: {
            title: 'Invalidez permanente total por acidente ou doença',
            description: 'Receba até R$ 20.000 se um acidente grave prejudicar sua rotina.',
        },
        Zurich1: {
            title: 'Invalidez permanente total por acidente ou doença',
            description: 'Receba até R$ 20.000 se um acidente grave prejudicar sua rotina.',
        },
    },
    funeral_assistance_consignated: {
        title: 'Auxílio funeral',
        description: 'Auxílio para o funeral do segurado, independente da causa mortis, na forma de reembolso de custos.',
    },
    medical_assistance_consignated: {
        title: 'Assistência em Medicamentos',
        description: 'Pronto atendimento para indicar farmácias cadastradas e até 80% de desconto em medicamentos.',
    },
    critical_illness: {
        hasTopics: true,
        standard: {
            title: 'Doenças graves',
            description: 'Cobertura de até R$ 20.000 para ajudar no seu tratamento.',
        },
        Zurich1: {
            title: 'Doenças graves',
            description: 'Cobertura de até R$ 20.000 para ajudar no seu tratamento.',
        },
    },
};

const getCoverageDisplay = (risk: string, insuranceEnum: string = 'standard'): { title: string; description: string } | null => {
    const data = COVERED_RISKS[risk];
    if (!data) return null;
    if (data.hasTopics) {
        const variant = data[insuranceEnum] || data['standard'];
        return variant ? { title: variant.title, description: variant.description } : null;
    }
    return { title: data.title, description: data.description };
};

const getBenefits = (insuranceEnum: string): string[] => {
    switch (insuranceEnum) {
        case '180_seguros':
        case 'Zurich1':
            return ['Sorteios mensais de R$ 10.000,00'];
        case 'Akad_Seguros1':
        case 'Akad_Seguros2':
        case 'Akad_Seguros3':
            return ['Sorteio mensal de R$ 5.000,00'];
        default:
            return [];
    }
};

const filterAndSortRisks = (risksKeys: string[]): string[] => {
    if (!risksKeys || !Array.isArray(risksKeys)) return [];

    let filtered = [...risksKeys];

    if (filtered.includes('permanent_disability_illness') || filtered.includes('permanent_disability_fgts')) {
        filtered = filtered.filter((r) => r !== 'permanent_disability');
    }

    const getPriority = (key: string): number => {
        switch (key) {
            case 'permanent_disability_illness':
            case 'permanent_disability_fgts':
            case 'permanent_disability':
                return 1;
            case 'critical_illness':
                return 2;
            case 'death':
                return 3;
            case 'unemployment':
            case 'involuntary_unemployment':
            case 'income_loss_fgts':
                return 4;
            default:
                return 1000;
        }
    };

    return filtered.sort((a, b) => getPriority(a) - getPriority(b));
};

// ── Account terms content items ──────────────────────────

const ACCOUNT_TERM_ITEMS = [
    {
        label: 'Custo Zero',
        text: 'Você não paga nenhuma taxa de abertura ou manutenção.',
    },
    {
        label: 'Uso Exclusivo',
        text: 'Serve apenas para movimentar valores do seu seguro (pagamentos e indenizações).',
    },
    {
        label: 'Organização',
        text: 'Seus recursos ficam protegidos e separados de outras contas.',
    },
    {
        label: 'Praticidade',
        text: 'Tudo acontece de forma automática e transparente, conforme as normas do BACEN e SUSEP.',
    },
];

// ── Component ────────────────────────────────────────────

export const PrivatePayrollAuctionResumeOperation = ({
    signData,
    setSignerStep,
    setSignData,
}: ResumeOperationProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);
    const text = resolveColor('text', clientThemeSettings);
    const initialError = { has_error: false, status: 0 };

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(false);
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [isChecked, setIsChecked] = useState(false);

    // Insurance state
    const insurance = signData?.insurance_data;
    const hasInsurance = !!insurance;
    const isInsuranceOptional = hasInsurance && !!signData?.insurance_decision_enabled;
    const insuranceEnum: string = insurance?.insurance_data_enum || 'standard';
    const showAccountTerms = hasInsurance && (insurance.payment_type === 'internal_account' || !!insurance.account_term_document_url);

    const [coverageOpen, setCoverageOpen] = useState(false);
    const [benefitsOpen, setBenefitsOpen] = useState(false);
    const [accountTermsOpen, setAccountTermsOpen] = useState(false);

    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom([]);
    const checkboxModal = useAnimatedModal();
    const rejectModal = useAnimatedModal();

    const op = signData?.credit_operation_data;
    const documentUrl = signData?.document_url;
    const borrowerName = op?.borrower?.name ?? '';
    const borrowerDocument = op?.borrower?.document_number
        ? cpfMask(op.borrower.document_number)
        : '';

    const benefits = hasInsurance ? getBenefits(insuranceEnum) : [];
    const sortedRisks = hasInsurance ? filterAndSortRisks(insurance.covered_risks) : [];

    const allCheckboxesMet = (() => {
        if (!isChecked) return false;
        return true;
    })();

    const handleDocumentDownload = async (url: string, filename: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(blobUrl);
        } catch {
            window.open(url, '_blank');
        }
    };

    const handleSubmit = async (decision?: 'accepted' | 'rejected') => {
        if (hasMadeRequest) return;

        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        const payload: any = {
            step: 'resume_operation',
            event_date: new Date().toISOString(),
            device_data: { device_key: browserKey },
        };

        if (decision) {
            payload.decision = decision;
        }

        const config = { headers: { Authorization: signData.signer_token } };

        setLoading(true);
        setHasMadeRequest(true);

        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, ...response.data });
            })
            .catch((error) => {
                const status = [401, 403, 409].includes((error.response || {}).status)
                    ? error.response.status
                    : 500;
                setError({ has_error: true, status });
                setHasMadeRequest(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleButtonClick = () => {
        if (!hasScrolledToEnd) {
            scrollToBottom();
            return;
        }

        if (!allCheckboxesMet) {
            checkboxModal.open();
            return;
        }

        if (isInsuranceOptional) {
            handleSubmit('accepted');
        } else {
            handleSubmit();
        }
    };

    const handleRejectInsurance = () => {
        rejectModal.close();
        handleSubmit('rejected');
    };

    if (loading) return <PrivatePayrollAuctionLoading />;

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.ScrollContent>
                {/* ── Credit operation resume ── */}
                <S.Title $color={primary}>Resumo do contrato</S.Title>

                <S.DataCardContainer>
                    <S.DataRow>
                        <S.RowLabel>Valor total do empréstimo</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatCurrency(op?.issue_amount ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>IOF</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatCurrency(op?.iof ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Valor líquido a receber</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatCurrency(op?.disbursed_amount ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Taxa de juros mensal</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatPercent(op?.monthly_interest_rate ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Número de parcelas mensais</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {op?.number_of_installments ?? '-'}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Data da primeira parcela</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {op?.first_due_date ? formatDate(op.first_due_date) : '-'}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>CET (ao mês)</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatPercent(op?.monthly_cet ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    {op?.interest_amount != null && (
                        <S.DataRow>
                            <S.RowLabel>Juros a pagar</S.RowLabel>
                            <S.RowValue $color={primary}>
                                {formatCurrency(op.interest_amount)}
                            </S.RowValue>
                        </S.DataRow>
                    )}
                    {op?.total_amount != null && (
                        <S.DataRow>
                            <S.RowLabel>Total a pagar</S.RowLabel>
                            <S.RowValue $color={primary}>
                                {formatCurrency(op.total_amount)}
                            </S.RowValue>
                        </S.DataRow>
                    )}
                    {borrowerName && (
                        <S.DataRow>
                            <S.RowLabel>Beneficiário</S.RowLabel>
                            <S.RowValue $color={primary} $bold>{borrowerName}</S.RowValue>
                        </S.DataRow>
                    )}
                    {borrowerDocument && (
                        <S.DataRow>
                            <S.RowLabel>CPF</S.RowLabel>
                            <S.RowValue $color={primary} $bold>{borrowerDocument}</S.RowValue>
                        </S.DataRow>
                    )}
                </S.DataCardContainer>

                {documentUrl && (
                    <S.InfoCallout>
                        <S.InfoIcon $color={primary}>i</S.InfoIcon>
                        <S.InfoText $color={primary}>
                            Para mais informações,{' '}
                            <a href={documentUrl} onClick={(e) => { e.preventDefault(); handleDocumentDownload(documentUrl, 'contrato-emprestimo.pdf'); }}>
                                consulte o contrato de empréstimo.
                            </a>
                        </S.InfoText>
                    </S.InfoCallout>
                )}

                <S.CheckboxWrapper>
                    <CheckboxField
                        id="resume-operation-terms"
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                        checkboxColor={primary}
                        textColor={text}
                    >
                        <strong style={{color: primary}}>Confirmo que li e estou de acordo</strong> com as condições de contratação do meu empréstimo.
                    </CheckboxField>
                </S.CheckboxWrapper>

                {/* ── Insurance section ── */}
                {hasInsurance && (
                    <>
                        <S.Title $color={primary}>Proteção financeira</S.Title>

                        <S.InsurancePremiumCard>
                            <S.InsurancePremiumLabel>
                                {isInsuranceOptional ? 'Seguro opcional' : 'Seguro'}
                            </S.InsurancePremiumLabel>
                            <S.InsurancePremiumValue $color={primary}>
                                {formatCurrency(insurance.premium_amount)}
                            </S.InsurancePremiumValue>
                        </S.InsurancePremiumCard>

                        {/* Coberturas */}
                        {sortedRisks.length > 0 && (
                            <S.CollapsibleCard>
                                <S.CollapsibleHeader
                                    $borderBottom={coverageOpen}
                                    onClick={() => setCoverageOpen(!coverageOpen)}
                                >
                                    <S.CollapsibleTitle>Coberturas</S.CollapsibleTitle>
                                    <S.ChevronIcon $open={coverageOpen}>
                                        <ChevronDownIcon width={20} height={20} />
                                    </S.ChevronIcon>
                                </S.CollapsibleHeader>
                                <S.CollapsibleContent $open={coverageOpen}>
                                    <div>
                                        {sortedRisks.map((risk) => {
                                            const display = getCoverageDisplay(risk, insuranceEnum);
                                            if (!display) return null;
                                            return (
                                                <S.CoverageItem key={risk}>
                                                    <S.CoverageTitle>{display.title}</S.CoverageTitle>
                                                    <S.CoverageDescription>{display.description}</S.CoverageDescription>
                                                </S.CoverageItem>
                                            );
                                        })}
                                    </div>
                                </S.CollapsibleContent>
                            </S.CollapsibleCard>
                        )}

                        {/* Beneficios */}
                        {benefits.length > 0 && (
                            <S.CollapsibleCard>
                                <S.CollapsibleHeader
                                    $borderBottom={benefitsOpen}
                                    onClick={() => setBenefitsOpen(!benefitsOpen)}
                                >
                                    <S.CollapsibleTitle>Benefícios</S.CollapsibleTitle>
                                    <S.ChevronIcon $open={benefitsOpen}>
                                        <ChevronDownIcon width={20} height={20} />
                                    </S.ChevronIcon>
                                </S.CollapsibleHeader>
                                <S.CollapsibleContent $open={benefitsOpen}>
                                    <div>
                                        {benefits.map((benefit, i) => (
                                            <S.BenefitItem key={i}>
                                                <S.BenefitText $color={primary}>
                                                    {benefit.split(/(R\$\s[\d.,]+)/).map((part, j) =>
                                                        /R\$\s[\d.,]+/.test(part) ? <strong key={j}>{part}</strong> : part
                                                    )}
                                                </S.BenefitText>
                                            </S.BenefitItem>
                                        ))}
                                    </div>
                                </S.CollapsibleContent>
                            </S.CollapsibleCard>
                        )}
                        {/* Termos de abertura de conta */}
                        {showAccountTerms && (
                            <>
                                <S.CollapsibleCard>
                                    <S.CollapsibleHeader
                                        $borderBottom={accountTermsOpen}
                                        onClick={() => setAccountTermsOpen(!accountTermsOpen)}
                                    >
                                        <S.CollapsibleTitle>Termo de abertura de conta</S.CollapsibleTitle>
                                        <S.ChevronIcon $open={accountTermsOpen}>
                                            <ChevronDownIcon width={20} height={20} />
                                        </S.ChevronIcon>
                                    </S.CollapsibleHeader>
                                    <S.CollapsibleContent $open={accountTermsOpen}>
                                        <div>
                                            <S.CollapsibleBody>
                                                <S.AccountTermIntro>
                                                    Para garantir a segurança e agilidade do seu seguro,{' '}
                                                    <strong>criamos uma conta digital vinculada ao seu crédito.</strong>
                                                </S.AccountTermIntro>
                                                {ACCOUNT_TERM_ITEMS.map((item, i) => (
                                                    <S.AccountTermItem key={i}>
                                                        <S.AccountTermLabel>{item.label}</S.AccountTermLabel>
                                                        <S.AccountTermText>{item.text}</S.AccountTermText>
                                                    </S.AccountTermItem>
                                                ))}
                                            </S.CollapsibleBody>
                                        </div>
                                    </S.CollapsibleContent>
                                </S.CollapsibleCard>
                            </>
                        )}

                        {insurance.term_document_url && (
                            <S.InfoCallout>
                                <S.InfoIcon $color={primary}>i</S.InfoIcon>
                                <S.InfoText $color={primary}>
                                    Para mais informações, consulte o {' '}
                                    <a href={insurance.term_document_url} onClick={(e) => { e.preventDefault(); handleDocumentDownload(insurance.term_document_url, 'contrato-seguro.pdf'); }}>
                                        contrato de seguro
                                    </a>
                                    {insurance.account_term_document_url &&
                                        <>
                                            {' '}e o{' '}
                                            <a href={insurance.account_term_document_url} onClick={(e) => { e.preventDefault(); handleDocumentDownload(insurance.account_term_document_url, 'termo-abertura-conta.pdf'); }}>
                                                termo de abertura de conta.
                                            </a>
                                        </>
                                    }
                                </S.InfoText>
                            </S.InfoCallout>
                        )}

                        {/* <S.CheckboxWrapper>
                            <CheckboxField
                                id="insurance-terms"
                                checked={insuranceTermsChecked}
                                onChange={(e) => setInsuranceTermsChecked(e.target.checked)}
                                checkboxColor={primary}
                                textColor={text}
                            >
                                Concordo com o <strong style={{color: primary}}>Termo de contratação do seguro</strong> e com o <strong style={{color: primary}}>Termo de abertura de conta</strong>
                            </CheckboxField>
                        </S.CheckboxWrapper> */}
                    </>
                )}

                <div ref={contentEndRef} />
            </S.ScrollContent>

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}

            <S.Footer>
                <S.SubmitButton
                    $backgroundColor={primary}
                    $disabled={false}
                    onClick={handleButtonClick}
                >
                    {hasInsurance ? 'Continuar' : 'Aceitar termos do contrato'}
                    <ChevronRightIcon width={24} height={24} />
                </S.SubmitButton>

                {isInsuranceOptional && (
                    <S.TextButton $color={primary} onClick={rejectModal.open}>
                        Assinar proposta sem proteção
                        <ChevronRightIcon width={18} height={18} />
                    </S.TextButton>
                )}

                <PoweredByQITech />
            </S.Footer>

            {/* Checkbox reminder modal */}
            {checkboxModal.show && (
                <S.ModalOverlay $closing={checkboxModal.isClosing} onClick={checkboxModal.close}>
                    <S.ModalSheet $closing={checkboxModal.isClosing} onClick={(e) => e.stopPropagation()}>
                        <S.SheetHandle />
                        <S.ModalTitle $color={primary}>Confirmação pendente</S.ModalTitle>
                        <S.ModalText>
                            Para continuar, você precisa{' '}
                            <strong>marcar todas as caixas de confirmação</strong> acima.
                        </S.ModalText>
                        <S.ModalButtonRow>
                            <S.SubmitButton
                                $backgroundColor={primary}
                                $disabled={false}
                                onClick={checkboxModal.close}
                            >
                                Entendi
                                <ChevronRightIcon width={24} height={24} />
                            </S.SubmitButton>
                        </S.ModalButtonRow>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}

            {/* Reject insurance confirmation modal */}
            {rejectModal.show && (
                <S.ModalOverlay $closing={rejectModal.isClosing} onClick={rejectModal.close}>
                    <S.ModalSheet $closing={rejectModal.isClosing} onClick={(e) => e.stopPropagation()}>
                        <S.SheetHandle />
                        <S.WarningIconWrapper>
                            <S.ModalTitle $color={primary}>
                                <Warning3Icon size={24} />
                                Continuar sem proteção
                            </S.ModalTitle>
                        </S.WarningIconWrapper>
                        <S.ModalText>
                            Deseja continuar a contratação do seu empréstimo sem a proteção financeira?
                        </S.ModalText>
                        <S.ModalButtonRow>
                            <S.SubmitButton
                                $backgroundColor={primary}
                                $disabled={false}
                                onClick={rejectModal.close}
                            >
                                Revisar as vantagens da proteção
                                <ChevronRightIcon width={24} height={24} />
                            </S.SubmitButton>
                            <S.TextButton $color={primary} onClick={handleRejectInsurance}>
                                Continuar a assinatura sem proteção
                                <ChevronRightIcon width={18} height={18} />
                            </S.TextButton>
                        </S.ModalButtonRow>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}
        </S.PageWrapper>
    );
};
